import { ACCESS_TOKEN_STORAGE_KEY } from "@frontend-shared/constants";
import {
  EAnalyticsAuthMethod,
  EAnalyticsEvent,
} from "@frontend-shared/product-analytics";
import type { IAcceptInviteData } from "@frontend-shared/types";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authApi } from "@/api/services";
import { useAuth } from "@/hooks/use-auth";
import { useProductAnalytics } from "@/hooks/use-product-analytics";
import { removeTabProjectUuid } from "@/lib/auth";

export const useAcceptInvite = () => {
  const { t } = useTranslation("auth");
  const { getUser } = useAuth();
  const { capture } = useProductAnalytics();

  return useMutation({
    mutationFn: (params: IAcceptInviteData) => authApi.acceptInvite(params),
    onSuccess: (data) => {
      // NOTE: Accepting logs the invitee straight in — same session bootstrap
      // as sign-in (persist the access token, then hydrate the user).
      // NOTE: NSAI-7394 — a new identity starts without this tab's old project
      // pointer, which a logout in another tab may not have reached.
      removeTabProjectUuid();
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
      // NOTE: NSAI-4615 — an invitee joining an existing workspace is a
      // signup, and a materially different one: they arrive with a team
      // around them, so their activation curve should not be blended with
      // self-serve signups.
      capture({
        event: EAnalyticsEvent.AUTH_SIGNUP_COMPLETED,
        properties: { method: EAnalyticsAuthMethod.INVITE },
      });
      getUser();
    },
    meta: {
      fallbackMessage: t("accept_invite.error"),
    },
  });
};
